import { z } from 'zod'

/**
 * FO-128 (batch-24) — Materieel-abonnementen. Gedeeld tussen API (validatie +
 * read-DTO) en backoffice (form-validatie + lijst/detail-rendering). Eén bron.
 *
 * Leent het ClientContract-patroon (FO-093) maar is een APART model: een
 * terugkerend maandbedrag gekoppeld aan een leverancier (Client type LEVERANCIER)
 * en optioneel een medewerker, met looptijd/opzegtermijn/indexering.
 *
 * Bedragen reizen als Decimal-string (nooit Float); `btwPercentage` is een FRACTIE
 * (0.21 = 21%, conform CLAUDE.md); `maandbedragInclBtw` is server-afgeleid
 * (excl × (1 + btw)). Datums als jjjj-mm-dd; DB slaat UTC op, verloop/opzegtermijn
 * worden in Europe/Amsterdam afgeleid.
 */

// ─── Enums (string-unions, gelijk aan de Prisma-enums) ──────────────────────

// FO-314 (batch-34) — één tuple als bron voor zowel de union als de Zod-enums,
// zodat de types (incl. de nieuwe LEASE/VERZEKERING/ONDERHOUDSCONTRACT) nergens
// dubbel gehardcodeerd staan.
export const ABONNEMENT_TYPE_TUPLE = [
  'TELEFOON',
  'DATA',
  'SOFTWARE_LICENTIE',
  'OVERIG',
  'LEASE',
  'VERZEKERING',
  'ONDERHOUDSCONTRACT',
] as const

export type AbonnementType = (typeof ABONNEMENT_TYPE_TUPLE)[number]

// batch-62 telefoonabonnementen (FO-463) — GEBLOKKEERD (SIM geblokkeerd bij de
// provider, bv. na verlies; het contract loopt en wordt gefactureerd). ADD VALUE-
// migratie `b62_telefoon_enums` (CC.2). Eén tuple voor union + Zod-enums.
export const ABONNEMENT_STATUS_TUPLE = ['CONCEPT', 'ACTIEF', 'OPGEZEGD', 'VERLOPEN', 'GEBLOKKEERD'] as const

export type AbonnementStatus = (typeof ABONNEMENT_STATUS_TUPLE)[number]

export type AbonnementAuditActie =
  | 'AANGEMAAKT'
  | 'BIJGEWERKT'
  | 'STATUS_GEWIJZIGD'
  | 'VERLENGD'
  | 'OPGEZEGD'
  | 'VERWIJDERD'
  // FO-314 (batch-34) — nieuwe acties.
  | 'DOCUMENT_GEKOPPELD'
  | 'DOCUMENT_ONTKOPPELD'
  | 'UNIEK_ITEM_GEKOPPELD'
  | 'UNIEK_ITEM_ONTKOPPELD'
  | 'OMGEHANGEN'
  | 'GEINDEXEERD'
  | 'FACTUUR_GEKOPPELD'
  | 'FACTUUR_ONTKOPPELD'
  // batch-62 telefoonabonnementen (FO-463) — de verloop-cron verlengde een
  // stilzwijgend contract (het moment van verlengen zichtbaar in de historie).
  | 'STILZWIJGEND_VERLENGD'

export const ABONNEMENT_TYPES: readonly AbonnementType[] = ABONNEMENT_TYPE_TUPLE

export const ABONNEMENT_TYPE_LABELS: Record<AbonnementType, string> = {
  TELEFOON: 'Telefoon',
  DATA: 'Data',
  SOFTWARE_LICENTIE: 'Softwarelicentie',
  OVERIG: 'Overig',
  LEASE: 'Lease',
  VERZEKERING: 'Verzekering',
  ONDERHOUDSCONTRACT: 'Onderhoudscontract',
}

export const ABONNEMENT_STATUSES: readonly AbonnementStatus[] = ABONNEMENT_STATUS_TUPLE

export const ABONNEMENT_STATUS_LABELS: Record<AbonnementStatus, string> = {
  CONCEPT: 'Concept',
  ACTIEF: 'Actief',
  OPGEZEGD: 'Opgezegd',
  VERLOPEN: 'Verlopen',
  GEBLOKKEERD: 'Geblokkeerd',
}

// Badge-varianten gelijk aan @streep/ui-fp Badge ('destructive', niet 'danger').
export const ABONNEMENT_STATUS_BADGE: Record<
  AbonnementStatus,
  'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'info'
> = {
  CONCEPT: 'secondary',
  ACTIEF: 'success',
  OPGEZEGD: 'secondary',
  VERLOPEN: 'destructive',
  GEBLOKKEERD: 'warning',
}

/**
 * Statussen waarin het contract LOOPT (en dus gefactureerd wordt): actief of
 * geblokkeerd. CONCEPT is nog niet in gebruik genomen; OPGEZEGD/VERLOPEN zijn
 * terminaal voor het status-endpoint.
 */
export const ABONNEMENT_LOPENDE_STATUSSEN: readonly AbonnementStatus[] = ['ACTIEF', 'GEBLOKKEERD']

// ─── batch-62 telefoonabonnementen (FO-463) — verlengingsregime ──────────────
//
// STILZWIJGEND = het contract verlengt vanzelf op de einddatum (de verloop-cron
// schuift de einddatum door met `verlengingsPeriodeMaanden`, anders de looptijd,
// anders 12 maanden); VAST = het contract loopt af op de einddatum. Leeg = niet
// vastgelegd (gedraagt zich als VAST: bestaand FO-128-gedrag).
export const ABONNEMENT_VERLENGINGSREGIMES = ['STILZWIJGEND', 'VAST'] as const
export type AbonnementVerlengingsregime = (typeof ABONNEMENT_VERLENGINGSREGIMES)[number]

export const ABONNEMENT_VERLENGINGSREGIME_LABELS: Record<AbonnementVerlengingsregime, string> = {
  STILZWIJGEND: 'Stilzwijgend verlengen',
  VAST: 'Vaste looptijd (loopt af)',
}

/**
 * Types waarvoor kosten, leverancier (provider) en ingangsdatum LATER ingevuld
 * mogen worden (FO-463 §5.5 + A2): het telefonie-register moet alle nummers kunnen
 * vastleggen voordat de PO contract en kosten kent. Voor de overige types blijft
 * het FO-128-gedrag: leverancier, maandbedrag en ingangsdatum verplicht.
 */
export const ABONNEMENT_TYPES_LATER_INVULBAAR: readonly AbonnementType[] = ['TELEFOON', 'DATA']

export function isLaterInvulbaarType(type: AbonnementType): boolean {
  return ABONNEMENT_TYPES_LATER_INVULBAAR.includes(type)
}

export const ABONNEMENT_AUDIT_LABELS: Record<AbonnementAuditActie, string> = {
  AANGEMAAKT: 'Aangemaakt',
  BIJGEWERKT: 'Bijgewerkt',
  STATUS_GEWIJZIGD: 'Status gewijzigd',
  VERLENGD: 'Verlengd',
  OPGEZEGD: 'Opgezegd',
  VERWIJDERD: 'Verwijderd',
  DOCUMENT_GEKOPPELD: 'Document gekoppeld',
  DOCUMENT_ONTKOPPELD: 'Document ontkoppeld',
  UNIEK_ITEM_GEKOPPELD: 'Toestel/SIM gekoppeld',
  UNIEK_ITEM_ONTKOPPELD: 'Toestel/SIM ontkoppeld',
  OMGEHANGEN: 'Omgehangen',
  GEINDEXEERD: 'Geïndexeerd',
  FACTUUR_GEKOPPELD: 'Factuur gekoppeld',
  FACTUUR_ONTKOPPELD: 'Factuur ontkoppeld',
  STILZWIJGEND_VERLENGD: 'Stilzwijgend verlengd',
}

// ─── Factuurcontrole-periode + afwijkingsdrempel (FO-314) ────────────────────

export const ABONNEMENT_FACTUUR_PERIODES = ['MAAND', 'KWARTAAL', 'JAAR'] as const
export type AbonnementFactuurPeriode = (typeof ABONNEMENT_FACTUUR_PERIODES)[number]

export const ABONNEMENT_FACTUUR_PERIODE_LABELS: Record<AbonnementFactuurPeriode, string> = {
  MAAND: 'Per maand',
  KWARTAAL: 'Per kwartaal',
  JAAR: 'Per jaar',
}

/** Deler om een factuurbedrag naar een maandbedrag te normaliseren (JAAR = /12). */
export const ABONNEMENT_FACTUUR_PERIODE_MAANDEN: Record<AbonnementFactuurPeriode, number> = {
  MAAND: 1,
  KWARTAAL: 3,
  JAAR: 12,
}

/**
 * Afwijkingsdrempel voor de factuurcontrole (FO-314 §5). Een afwijking telt als
 * signaal-waardig zodra hij BOVEN de euro-drempel OF de procent-drempel uitkomt.
 * Instelbaar-default; gedeelde constante zodat API-signaal en UI dezelfde grens
 * hanteren (CC.0b: geen aparte settings-UI in deze iteratie).
 */
export const ABONNEMENT_AFWIJKING_DREMPEL_EUR = 0.5
export const ABONNEMENT_AFWIJKING_DREMPEL_PCT = 0.02

/** True als |afwijking| de drempel (EUR of %) overschrijdt t.o.v. het maandbedrag. */
export function abonnementAfwijkingBovenDrempel(
  afwijkingBedrag: number,
  maandbedragExcl: number,
): boolean {
  const abs = Math.abs(afwijkingBedrag)
  if (abs > ABONNEMENT_AFWIJKING_DREMPEL_EUR) return true
  if (maandbedragExcl > 0 && abs / maandbedragExcl > ABONNEMENT_AFWIJKING_DREMPEL_PCT) return true
  return false
}

// ─── Status-workflow ─────────────────────────────────────────────────────────
//
// CONCEPT -> ACTIEF (activeren) -> OPGEZEGD (opzeggen) of VERLOPEN (cron/handmatig
// bij gepasseerde einddatum). Heractiveren van een OPGEZEGD/VERLOPEN abonnement
// gaat via "verlengen" (nieuwe einddatum) en zet de status terug op ACTIEF.
// batch-62 (FO-463): ACTIEF <-> GEBLOKKEERD (SIM geblokkeerd/gedeblokkeerd bij de
// provider); een geblokkeerd abonnement kan ook opgezegd worden of verlopen.

/** Geldige overgangen via het status-endpoint (verlengen heeft zijn eigen endpoint). */
export function isValidAbonnementTransition(
  from: AbonnementStatus,
  to: AbonnementStatus,
): boolean {
  if (from === to) return false
  if (from === 'CONCEPT') return to === 'ACTIEF'
  if (from === 'ACTIEF') return to === 'OPGEZEGD' || to === 'VERLOPEN' || to === 'GEBLOKKEERD'
  if (from === 'GEBLOKKEERD') return to === 'ACTIEF' || to === 'OPGEZEGD' || to === 'VERLOPEN'
  // OPGEZEGD/VERLOPEN zijn terminaal voor het status-endpoint; heractiveren = verlengen.
  return false
}

// ─── Zod-schemas (gedeeld API + form) ───────────────────────────────────────

const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum moet jjjj-mm-dd zijn')

// Bedragen als Decimal-string (de form normaliseert komma -> punt vóór versturen).
const geldBedrag = z
  .string()
  .trim()
  .regex(/^\d+(\.\d{1,2})?$/, 'Bedrag moet een geldig getal zijn (bv. 35.00)')

const percentage = z
  .string()
  .trim()
  .regex(/^\d+(\.\d{1,2})?$/, 'Percentage moet een geldig getal zijn (bv. 2.50)')

// Btw als FRACTIE (0.21 = 21%, conform CLAUDE.md "Geld en getallen"), 0..1.
const btwFractie = z
  .string()
  .trim()
  .regex(/^\d+(\.\d{1,4})?$/, 'Btw moet een fractie zijn (bv. 0.21 voor 21%)')
  .refine((s) => {
    const n = Number(s)
    return Number.isFinite(n) && n >= 0 && n <= 1
  }, 'Btw-fractie moet tussen 0 en 1 liggen (0.21 = 21%)')

/**
 * batch-62 (FO-463) — telefoonnummer zoals ingevoerd (elk NL-formaat of E.164).
 * De server normaliseert naar E.164 met `normaliseerTelefoonnummer` (@streep/utils)
 * en geeft een inline veldfout `nummer` als dat niet lukt; het form toont met
 * dezelfde helper vóór verzenden al de genormaliseerde vorm/fout.
 */
const telefoonnummerInvoer = z
  .string()
  .trim()
  .max(40, 'Telefoonnummer is te lang')
  .regex(/^[+0-9()\s./-]*$/, 'Telefoonnummer mag alleen cijfers, spaties, +, -, ( en ) bevatten')

/** ICCID (SIM-nummer) zoals ingevoerd; de server normaliseert en valideert (89…, 18-22 cijfers). */
const iccidInvoer = z.string().trim().max(40, 'ICCID is te lang')

const abonnementBase = z.object({
  naam: z.string().trim().min(1, 'Naam is verplicht'),
  type: z.enum(ABONNEMENT_TYPE_TUPLE),
  omschrijving: z.string().trim().nullable().optional(),
  // batch-62 (FO-463) — nullable: voor TELEFOON/DATA mag de provider later volgen
  // (de read-DTO geeft dan `null`; zie `isLaterInvulbaarType`). Voor de overige
  // types dwingt de create-refine hieronder + de route 'm af.
  leverancierId: z.string().min(1, 'Leverancier is verplicht').nullable().optional(),
  employeeId: z.string().min(1).nullable().optional(),
  maandbedragExclBtw: geldBedrag.nullable().optional(),
  btwPercentage: btwFractie,
  ingangsdatum: dateOnly.nullable().optional(),
  einddatum: dateOnly.nullable().optional(),
  looptijdMaanden: z.number().int().positive().nullable().optional(),
  opzegtermijnDagen: z.number().int().nonnegative().nullable().optional(),
  indexeringPct: percentage.nullable().optional(),
  // FO-314 — indexeringsmaand 1-12 (default januari in de app als er een pct is).
  indexeringsmaand: z.number().int().min(1).max(12).nullable().optional(),
  notitie: z.string().nullable().optional(),
  // ── batch-62 telefoonabonnementen (FO-463) ────────────────────────────────
  /** Telefoonnummer (matchsleutel, server-side naar E.164). */
  nummer: telefoonnummerInvoer.nullable().optional(),
  /** ICCID-referentie (tweede matchsleutel op providerfacturen). */
  iccid: iccidInvoer.nullable().optional(),
  /** BV (Entity-id) waar het abonnement onder valt. */
  entityId: z.string().min(1).nullable().optional(),
  verlengingsregime: z.enum(ABONNEMENT_VERLENGINGSREGIMES).nullable().optional(),
  /** Verlengingsperiode bij stilzwijgend verlengen (leeg = looptijd, anders 12). */
  verlengingsPeriodeMaanden: z.number().int().positive().max(120).nullable().optional(),
  /** FO-508 (spec 208) — klantnummer bij de provider; geen matchsleutel. */
  providerKlantnummer: z.string().trim().max(40, 'Klantnummer is te lang').nullable().optional(),
})

// einddatum mag niet vóór ingangsdatum liggen (jjjj-mm-dd vergelijkt chronologisch).
const einddatumNaIngang = (data: { ingangsdatum?: string | null; einddatum?: string | null }): boolean => {
  if (!data.ingangsdatum || !data.einddatum) return true
  return data.einddatum >= data.ingangsdatum
}
const einddatumError: { message: string; path: string[] } = {
  message: 'Einddatum mag niet vóór de ingangsdatum liggen',
  path: ['einddatum'],
}

export const CreateAbonnementSchema = abonnementBase
  .extend({
    // Initiële status: standaard CONCEPT; ACTIEF mag direct (geen dubbele stap).
    status: z.enum(['CONCEPT', 'ACTIEF']).optional(),
    /**
     * FO-508 (spec 208) — telefoon en simkaart als set: bij een telefoon-
     * abonnement dat direct op een medewerker komt, diens telefoon.
     */
    toestelId: z.string().min(1).nullable().optional(),
  })
  .refine(einddatumNaIngang, einddatumError)
  .superRefine((data, ctx) => {
    // FO-128-gedrag voor alles behalve het telefonie-register (FO-463 §5.5).
    if (isLaterInvulbaarType(data.type)) return
    if (!data.leverancierId) {
      ctx.addIssue({ code: 'custom', path: ['leverancierId'], message: 'Leverancier is verplicht' })
    }
    if (!data.maandbedragExclBtw) {
      ctx.addIssue({ code: 'custom', path: ['maandbedragExclBtw'], message: 'Maandbedrag is verplicht' })
    }
    if (!data.ingangsdatum) {
      ctx.addIssue({ code: 'custom', path: ['ingangsdatum'], message: 'Ingangsdatum is verplicht' })
    }
  })
export type CreateAbonnementInput = z.infer<typeof CreateAbonnementSchema>

export const UpdateAbonnementSchema = abonnementBase.partial().refine(einddatumNaIngang, einddatumError)
export type UpdateAbonnementInput = z.infer<typeof UpdateAbonnementSchema>

export const AbonnementStatusTransitionSchema = z.object({
  status: z.enum(ABONNEMENT_STATUS_TUPLE),
  toelichting: z.string().trim().max(2000).optional(),
})
export type AbonnementStatusTransitionInput = z.infer<typeof AbonnementStatusTransitionSchema>

export const VerlengAbonnementSchema = z.object({
  einddatum: dateOnly,
  // Optioneel nieuw maandbedrag (bv. na indexering) — leeg = ongewijzigd.
  maandbedragExclBtw: geldBedrag.optional(),
  btwPercentage: btwFractie.optional(),
  toelichting: z.string().trim().max(2000).optional(),
})
export type VerlengAbonnementInput = z.infer<typeof VerlengAbonnementSchema>

// ─── Read-DTOs ───────────────────────────────────────────────────────────────

export interface AbonnementAuditDTO {
  id: string
  actie: AbonnementAuditActie
  actorNaam: string | null
  oudeStatus: AbonnementStatus | null
  nieuweStatus: AbonnementStatus | null
  // FO-314 — bij OMGEHANGEN: de van/naar-medewerker (null = bedrijfsbreed).
  vanEmployeeNaam: string | null
  naarEmployeeNaam: string | null
  toelichting: string | null
  createdAt: string
}

export interface AbonnementListRowDTO {
  id: string
  naam: string
  type: AbonnementType
  omschrijving: string | null
  status: AbonnementStatus
  /** batch-62 (FO-463) — `null` = provider/leverancier nog niet bekend (TELEFOON/DATA). */
  leverancierId: string | null
  leverancier: { id: string; name: string } | null
  employeeId: string | null
  employee: { id: string; firstName: string; lastName: string } | null
  /**
   * Decimal-strings (nooit Float). `btwPercentage` is een fractie (0.21 = 21%).
   * batch-62 (FO-463) — `null` = bedrag nog niet ingevuld ("onvolledig").
   */
  maandbedragExclBtw: string | null
  btwPercentage: string
  maandbedragInclBtw: string | null
  /** batch-62 (FO-463) — `null` = contractstart nog niet bekend. */
  ingangsdatum: string | null
  einddatum: string | null
  // ── batch-62 telefoonabonnementen (FO-463) ────────────────────────────────
  /** Telefoonnummer in E.164 (`+31612345678`), de matchsleutel; `null` = geen nummer. */
  nummer: string | null
  /** Leesbare vorm (`06-12345678`). */
  nummerWeergave: string | null
  iccid: string | null
  /** FO-508 (spec 208) — klantnummer bij de provider. */
  providerKlantnummer: string | null
  entityId: string | null
  verlengingsregime: AbonnementVerlengingsregime | null
  verlengingsPeriodeMaanden: number | null
  looptijdMaanden: number | null
  opzegtermijnDagen: number | null
  indexeringPct: string | null
  /** FO-314 — maand (1-12) waarin de indexering-cron toepast; `null` = geen. */
  indexeringsmaand: number | null
  /** FO-314 — gekoppeld toestel/sim (VoorraadUniekItem-id); `null` = niet gekoppeld. */
  gekoppeldUniekItemId: string | null
  /** Dagen tot einddatum (Europe/Amsterdam); negatief = verlopen; `null` = geen einddatum. */
  dagenTotEinde: number | null
  /** Dagen tot de opzegdeadline (einddatum - opzegtermijn); `null` = geen einddatum. */
  dagenTotOpzegtermijn: number | null
}

export interface AbonnementDTO extends AbonnementListRowDTO {
  notitie: string | null
  opgezegdOp: string | null
  createdAt: string
  updatedAt: string
  audits: AbonnementAuditDTO[]
  // FO-314 — completering.
  gekoppeldUniekItem: AbonnementUniekItemDTO | null
  factuurRegels: AbonnementFactuurRegelDTO[]
  /** OPGEZEGD zonder opzegbevestiging-document → open taakje actief. */
  heeftOpenOpzegtaak: boolean
}

// ─── Portal-DTO (FO-314 §2.2 — GEEN bedragen) ────────────────────────────────

/**
 * Read-only kaartje voor "mijn bedrijfsmiddelen" in de portal. Bewust ZONDER
 * bedragen (kosten zijn geen monteur-zicht, FO-033); alleen wat de medewerker
 * moet weten dat er op zijn naam loopt. Server filtert strikt op de eigen
 * employeeId (tenancy).
 */
export interface MijnAbonnementDTO {
  id: string
  type: AbonnementType
  typeLabel: string
  /** batch-62 — `null` = provider nog niet bekend (telefoonnummer, later invulbaar). */
  leverancierNaam: string | null
  /** Bij een gekoppeld telefoon/SIM-item: het nummer (anders null). */
  telefoonnummer: string | null
  /** batch-62 — `null` = contractstart nog niet bekend. */
  ingangsdatum: string | null
  status: AbonnementStatus
}

// ─── Dossier-DTO (FO-314 §2.2 — bedragen achter canViewCostPrices) ───────────

/**
 * Rij in het "Abonnementen"-blok op de medewerker-bedrijfsmiddelen-tab. Bedragen
 * zijn `null` zonder `canViewCostPrices` (server-side geredigeerd); type/leverancier/
 * looptijd blijven altijd zichtbaar.
 */
export interface AbonnementDossierRowDTO {
  id: string
  naam: string
  type: AbonnementType
  status: AbonnementStatus
  leverancierNaam: string
  ingangsdatum: string | null
  einddatum: string | null
  dagenTotOpzegtermijn: number | null
  /** Decimal-strings of `null` als de viewer geen kostprijs-rechten heeft. */
  maandbedragExclBtw: string | null
  maandbedragInclBtw: string | null
  /** batch-62 (FO-463) — leesbaar telefoonnummer (`06-12345678`), anders `null`. */
  nummerWeergave: string | null
  /**
   * batch-62 (FO-463) — de laatst GEBOEKTE providerfactuur-maand op dit abonnement
   * (werkelijke kosten excl. btw, incl. buitenbundel). Bedrag `null` zonder
   * kostprijs-rechten (gevoelige data); `null` geheel = nog geen factuurregel.
   */
  laatstGefactureerd: { maand: string; bedragExclBtw: string | null } | null
}

export interface AbonnementDossierDTO {
  /** Of de viewer bedragen mag zien (server-bepaald via canViewCostPrices). */
  toonBedragen: boolean
  /** Of de viewer abonnement-acties mag uitvoeren (ASSETS.canEdit, server-bepaald). */
  canEdit: boolean
  rows: AbonnementDossierRowDTO[]
}

// ─── Lijst-query (filters) ───────────────────────────────────────────────────

export const AbonnementListQuerySchema = z.object({
  zoek: z.string().trim().optional(),
  type: z.enum(ABONNEMENT_TYPE_TUPLE).optional(),
  status: z.enum(ABONNEMENT_STATUS_TUPLE).optional(),
  leverancierId: z.string().min(1).optional(),
  employeeId: z.string().min(1).optional(),
})
export type AbonnementListQuery = z.infer<typeof AbonnementListQuerySchema>

// ─── FO-314 mutatie-schemas (omhangen / sim-koppeling / factuurcontrole) ─────

/**
 * Omhangen: het abonnement naar een andere medewerker (of bedrijfsbreed =
 * employeeId null) overzetten. `perDatum` = de ingangsdatum van de nieuwe
 * eigenaar-periode (default vandaag, server-side in Europe/Amsterdam); backdaten
 * mag zodat de kosten-actuals historisch kloppen.
 */
export const OmhangAbonnementSchema = z.object({
  employeeId: z.string().min(1).nullable(),
  perDatum: dateOnly.optional(),
  reden: z.string().trim().max(2000).nullable().optional(),
  /**
   * FO-508 (spec 208) — telefoon en simkaart als set: de telefoon van de nieuwe
   * medewerker die in dezelfde handeling aan dit nummer wordt gekoppeld.
   */
  toestelId: z.string().min(1).nullable().optional(),
})
export type OmhangAbonnementInput = z.infer<typeof OmhangAbonnementSchema>

/** Telefoon/SIM-koppeling: koppel (of ontkoppel met null) een uniek voorraad-item. */
export const KoppelUniekItemSchema = z.object({
  uniekItemId: z.string().min(1).nullable(),
  /** Negeer de "tweede abonnement op deze sim"-waarschuwing bewust. */
  bevestigTweede: z.boolean().optional(),
})
export type KoppelUniekItemInput = z.infer<typeof KoppelUniekItemSchema>

/** Factuurcontrole: koppel een inkoopfactuur-regel aan het abonnement. */
export const KoppelFactuurRegelSchema = z.object({
  inkoopFactuurRegelId: z.string().min(1),
  periode: z.enum(ABONNEMENT_FACTUUR_PERIODES).default('MAAND'),
})
export type KoppelFactuurRegelInput = z.infer<typeof KoppelFactuurRegelSchema>

// ─── FO-314 read-DTO's ───────────────────────────────────────────────────────

/** Gekoppeld toestel/sim (uit VoorraadUniekItem) op het abonnement-detail. */
export interface AbonnementUniekItemDTO {
  id: string
  artikelNaam: string
  categorie: string
  interneCode: string | null
  serienummer: string | null
  telefoonnummer: string | null
  iccid: string | null
  provider: string | null
  imei: string | null
  status: string
  employeeId: string | null
  employeeNaam: string | null
}

/**
 * batch-62 (FO-463) — waar een gekoppelde factuurregel vandaan komt: een gewone
 * inkoopfactuur-regel (FO-314, handmatig gekoppeld) of een regel uit de per-nummer-
 * specificatie van een providerfactuur (Vodafone/Odido, automatisch geboekt).
 */
export type AbonnementFactuurRegelBron = 'INKOOPREGEL' | 'TELEFOONSPECIFICATIE'

/** Eén gekoppelde inkoopfactuur-regel + afwijking t.o.v. het maandbedrag. */
export interface AbonnementFactuurRegelDTO {
  id: string
  bron: AbonnementFactuurRegelBron
  /** `null` bij een regel uit de telefoonspecificatie (die hangt niet aan een inkoopregel). */
  inkoopFactuurRegelId: string | null
  factuurId: string
  factuurNummer: string | null
  factuurdatum: string | null
  omschrijving: string
  periode: AbonnementFactuurPeriode
  /** batch-62 — kostensoort van een specificatie-regel (abonnement/verbruik/…); anders `null`. */
  kostensoort: TelefoonKostensoortCode | null
  /** batch-62 — boekmaand (`jjjj-mm`) van een specificatie-regel; anders `null`. */
  boekMaand: string | null
  /** Regelbedrag excl. btw (Decimal-string). */
  regelbedrag: string
  /** Regelbedrag genormaliseerd naar maand (Decimal-string). */
  maandbedragGefactureerd: string
  /**
   * Verschil (gefactureerd-maand - afgesproken-maand); Decimal-string, kan negatief.
   * `null` bij een specificatie-regel (daar geldt het verschil per nummer per maand,
   * zie de telefonie-historie) of zolang het maandbedrag nog niet is ingevuld.
   */
  afwijkingBedrag: string | null
  bovenDrempel: boolean
}

/**
 * De telefoon-kostensoorten als string-union (gespiegeld in `telefonie.ts` met
 * labels). Hier gedefinieerd zodat `abonnement.ts` geen circulaire import krijgt.
 */
export type TelefoonKostensoortCode =
  | 'ABONNEMENT'
  | 'VERBRUIK'
  | 'EENMALIG'
  | 'TOESTEL'
  | 'KORTING'
  | 'OVERIG'

/**
 * Response-hint bij het uitgeven van een telefoon/sim (FO §2.3): het gekoppelde
 * ACTIEVE abonnement staat op een ándere (of geen) medewerker → stel omhangen voor.
 * Geen automatisme; de UI toont een bevestiging.
 */
export interface AbonnementOmhangenSuggestieDTO {
  abonnementId: string
  abonnementNaam: string
  vanEmployeeId: string | null
  naarEmployeeId: string
}

/** Kandidaat-factuurregel als koppel-suggestie (leverancier + omschrijving-match). */
export interface AbonnementFactuurSuggestieDTO {
  inkoopFactuurRegelId: string
  factuurId: string
  factuurNummer: string | null
  factuurdatum: string | null
  omschrijving: string
  /** Regelbedrag excl. btw (Decimal-string). */
  regelbedrag: string
  /** Heuristische match: exacte genormaliseerde omschrijving-match op eerdere regels. */
  omschrijvingMatch: boolean
}
